// ===== BEGIN FILE: src/components/HousePredictionsTable.tsx =====
import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Table,
  Typography,
  Sheet,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Card,
} from '@mui/joy';
import { Info, CheckCircle, Warning, Lightbulb, ContentCopy, Download } from '@mui/icons-material';
import { supabase } from '../utils/supabaseClient';

type PlanetName =
  | 'Sun' | 'Moon' | 'Mars' | 'Mercury' | 'Jupiter' | 'Venus' | 'Saturn'
  | 'Rahu' | 'Ketu';

type HousePrediction = {
  house: number;
  houseName: string;
  planet: PlanetName;
  blendedPct?: number;
  actionable: string | null;
  caution: string | null;
  remedy: string | null;
};

type Props = {
  placementsD1: Record<PlanetName, { house: number; offsetDeg: number }>;
  getPlanetColor: (planet: PlanetName) => string;
  blendedPct?: Partial<Record<PlanetName, number>>;
  userRole?: 'admin' | 'member';
};

const HOUSE_NAMES: Record<number, string> = {
  1: 'Ascendant (Self)',
  2: 'Wealth & Family',
  3: 'Courage & Siblings',
  4: 'Home & Mother',
  5: 'Children & Creativity',
  6: 'Health & Enemies',
  7: 'Partnership & Marriage',
  8: 'Transformation & Longevity',
  9: 'Fortune & Dharma',
  10: 'Career & Status',
  11: 'Gains & Aspirations',
  12: 'Loss & Liberation',
};

const PLANET_ORDER: PlanetName[] = [
  'Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn', 'Rahu', 'Ketu'
];

const HousePredictionsTable: React.FC<Props> = ({ placementsD1, getPlanetColor, blendedPct, userRole }) => {
  const [predictions, setPredictions] = useState<HousePrediction[]>([]);
  const [loading, setLoading] = useState(true);
  const tableRef = useRef<HTMLDivElement>(null);

  // Copy table to clipboard
  const handleCopyTable = () => {
    if (predictions.length === 0) return;

    let text = 'HOUSE-WISE PREDICTIONS\n\n';
    text += 'House\tPlanet\tActionable\tCaution\tRemedy\n';
    text += '─'.repeat(80) + '\n';

    predictions.forEach(pred => {
      text += `${pred.house} - ${pred.houseName}\t`;
      text += `${pred.planet}\t`;
      text += `${pred.actionable || 'N/A'}\t`;
      text += `${pred.caution || 'N/A'}\t`;
      text += `${pred.remedy || 'N/A'}\n`;
    });

    navigator.clipboard.writeText(text).then(() => {
      alert('Table copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy:', err);
      alert('Failed to copy table');
    });
  };

  // Export to PDF using browser print (same as HousePlanetInterpretations)
  const handleExportPDF = () => {
    const node = tableRef.current;
    if (!node) return;
    
    const w = window.open('', '_blank');
    if (!w) return;
    
    w.document.write('<html><head><title>House-wise Predictions</title>');
    w.document.write('<style>body{font-family: system-ui, Segoe UI, Roboto, Helvetica, Arial; padding:12px} table{width:100%; border-collapse:collapse} th,td{border:1px solid #ddd; padding:8px; vertical-align:top; text-align:left} th{background:#f7f7f7; font-weight:600}</style>');
    w.document.write('</head><body>');
    w.document.write('<h1>House-wise Predictions</h1>');
    w.document.write(node.innerHTML);
    w.document.write('</body></html>');
    w.document.close();
    w.focus();
    w.print();
  };

  useEffect(() => {
    const fetchPredictions = async () => {
      setLoading(true);
      try {
        // Get all houses data from cache or fetch
        const cacheKey = 'houses_all_v1';
        const cached = localStorage.getItem(cacheKey);
        
        let houses;
        if (cached) {
          houses = JSON.parse(cached);
        } else {
          const { data, error } = await supabase
            .from('houses')
            .select('*')
            .order('id', { ascending: true });

          if (error) throw error;
          if (!data) return;
          
          houses = data;
          localStorage.setItem(cacheKey, JSON.stringify(houses));
        }

        // Build predictions for each planet placement
        const predictionsList: HousePrediction[] = [];

        PLANET_ORDER.forEach((planet) => {
          const placement = placementsD1[planet];
          if (!placement) return;

          const houseNum = placement.house;
          const houseData = houses.find((h: any) => h.id === houseNum);
          if (!houseData) return;

          const planetLower = planet.toLowerCase();
          const actionableKey = `actionable_${planetLower}`;
          const cautionKey = `caution_${planetLower}`;
          const remedyKey = `remedy_${planetLower}`;

          predictionsList.push({
            house: houseNum,
            houseName: HOUSE_NAMES[houseNum] || `House ${houseNum}`,
            planet,
            blendedPct: blendedPct?.[planet],
            actionable: (houseData as any)[actionableKey] || null,
            caution: (houseData as any)[cautionKey] || null,
            remedy: (houseData as any)[remedyKey] || null,
          });
        });

        setPredictions(predictionsList);
      } catch (err) {
        console.error('Error fetching house predictions:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPredictions();
  }, [placementsD1, blendedPct]);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography level="body-sm">Loading predictions...</Typography>
      </Box>
    );
  }

  if (predictions.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 3 }} ref={tableRef}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography level="h4" sx={{ fontWeight: 600 }}>
          House-wise Predictions
        </Typography>
        
        {userRole === 'admin' && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Copy table to clipboard">
              <Button
                size="sm"
                variant="outlined"
                color="neutral"
                startDecorator={<ContentCopy />}
                onClick={handleCopyTable}
              >
                Copy
              </Button>
            </Tooltip>
            
            <Tooltip title="Export as PDF (browser print)">
              <Button
                size="sm"
                variant="outlined"
                color="neutral"
                startDecorator={<Download />}
                onClick={handleExportPDF}
              >
                PDF
              </Button>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Mobile Card View */}
      <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', gap: 2 }}>
        {predictions.map((pred, idx) => (
          <Card key={idx} variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography level="title-md" sx={{ fontWeight: 600 }}>
                House {pred.house}: {pred.houseName}
              </Typography>
              <Chip
                size="sm"
                sx={{
                  bgcolor: `${getPlanetColor(pred.planet)}20`,
                  color: getPlanetColor(pred.planet),
                  border: `1px solid ${getPlanetColor(pred.planet)}`,
                  fontWeight: 600
                }}
              >
                {pred.planet}
              </Chip>
            </Box>

            {pred.actionable && (
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <CheckCircle sx={{ fontSize: '1rem', color: 'success.500' }} />
                  <Typography level="body-sm" sx={{ fontWeight: 600, color: 'success.700' }}>Actionable</Typography>
                </Box>
                <Typography level="body-sm" sx={{ pl: 2.5 }}>{pred.actionable}</Typography>
              </Box>
            )}

            {pred.caution && (
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Warning sx={{ fontSize: '1rem', color: 'warning.500' }} />
                  <Typography level="body-sm" sx={{ fontWeight: 600, color: 'warning.700' }}>Caution</Typography>
                </Box>
                <Typography level="body-sm" sx={{ pl: 2.5 }}>{pred.caution}</Typography>
              </Box>
            )}

            {pred.remedy && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  <Lightbulb sx={{ fontSize: '1rem', color: 'primary.500' }} />
                  <Typography level="body-sm" sx={{ fontWeight: 600, color: 'primary.700' }}>Remedy</Typography>
                </Box>
                <Typography level="body-sm" sx={{ pl: 2.5 }}>{pred.remedy}</Typography>
              </Box>
            )}
          </Card>
        ))}
      </Box>

      {/* Desktop Table View */}
      <Sheet
        variant="outlined"
        sx={{
          display: { xs: 'none', sm: 'block' },
          borderRadius: 'md',
          overflow: 'auto',
          maxHeight: '600px',
        }}
      >
        <Table
          borderAxis="bothBetween"
          stickyHeader
          sx={{
            '& thead th': {
              bgcolor: 'background.surface',
              fontWeight: 600,
            },
            '& tbody td': {
              verticalAlign: 'top',
            },
          }}
        >
          <thead>
            <tr>
              <th style={{ width: '80px', textAlign: 'center' }}>House</th>
              <th style={{ width: '100px', textAlign: 'center' }}>Planet</th>
              <th style={{ width: '30%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CheckCircle sx={{ fontSize: '1rem', color: 'success.500' }} />
                  Actionable
                </Box>
              </th>
              <th style={{ width: '30%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Warning sx={{ fontSize: '1rem', color: 'warning.500' }} />
                  Caution
                </Box>
              </th>
              <th style={{ width: '30%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Lightbulb sx={{ fontSize: '1rem', color: 'primary.500' }} />
                  Remedy
                </Box>
              </th>
            </tr>
          </thead>
          <tbody>
            {predictions.map((pred, idx) => (
              <tr key={idx}>
                <td>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                      {pred.house}
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                      {pred.houseName}
                    </Typography>
                  </Box>
                </td>
                <td>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Chip
                      size="sm"
                      variant="soft"
                      sx={{
                        bgcolor: getPlanetColor(pred.planet),
                        color: 'white',
                        fontWeight: 600,
                        minWidth: '70px',
                      }}
                    >
                      {pred.planet}
                    </Chip>
                    {pred.blendedPct !== undefined && (
                      <Typography 
                        level="body-xs" 
                        sx={{ 
                          fontWeight: 600, 
                          color: pred.blendedPct >= 0 ? '#2e7d32' : '#d32f2f'
                        }}
                      >
                        {pred.blendedPct >= 0 ? '+' : ''}{pred.blendedPct.toFixed(0)}%
                      </Typography>
                    )}
                  </Box>
                </td>
                <td>
                  {pred.actionable ? (
                    <Typography level="body-sm" sx={{ lineHeight: 1.6 }}>
                      {pred.actionable}
                    </Typography>
                  ) : (
                    <Typography level="body-xs" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                      No data
                    </Typography>
                  )}
                </td>
                <td>
                  {pred.caution ? (
                    <Typography level="body-sm" sx={{ lineHeight: 1.6 }}>
                      {pred.caution}
                    </Typography>
                  ) : (
                    <Typography level="body-xs" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                      No data
                    </Typography>
                  )}
                </td>
                <td>
                  {pred.remedy ? (
                    <Typography level="body-sm" sx={{ lineHeight: 1.6 }}>
                      {pred.remedy}
                    </Typography>
                  ) : (
                    <Typography level="body-xs" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                      No data
                    </Typography>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Sheet>

      <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Info sx={{ fontSize: '1rem', color: 'primary.500' }} />
        <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
          Predictions are based on planetary placements in houses according to Vedic astrology principles.
        </Typography>
      </Box>
    </Box>
  );
};

export default HousePredictionsTable;

// ===== END FILE: src/components/HousePredictionsTable.tsx =====
